import { useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useAttendance } from './AttendanceContext';
import { showModal, hideModal } from 'actions/modalActions';
import { getBalanceHours } from 'utils/datetime';
import moment from 'moment';

const buildHoursIndex = (hoursList) => {
    const map = {};
    if (!hoursList) return map;
    for (let i = 0; i < hoursList.length; i++) {
        const d = hoursList[i];
        const empId = d.employee?.id || d.employee_id;
        if (!empId || !d.day) continue;
        const key = `${empId}_${moment(d.day).format("YYYY-MM-DD")}`;
        map[key] = (map[key] || 0) + (d.value || 0);
    }
    return map;
};

export const useEmployeeDayTable = () => {
    const {
        items,
        loading,
        loadingMore,
        hasMore,
        fetchMoreData,
        handleSort,
        applySort,
        sortConfig,
        handleApprove,
        approvingIds,
        objectiveHours,
        plannedHours,
        plannedShifts,
        filters: { showRestTime },
        timeRegistrationBalanceCalculation,
    } = useAttendance();

    const dispatch = useDispatch();

    const handleHideModal = useCallback(() => {
        dispatch(hideModal());
    }, [dispatch]);

    const handleApprovalWithError = useCallback((id, approved) => {
        const record = items.find(item => item.id === id);
        if (!record.start || !record.end) {
            dispatch(
                showModal({
                    modalProps: {
                        open: true,
                        style: { width: "400px" },
                        title: "Error",
                        message: "Los marcajes tienen que tener inicio y fin para aprobarse",
                        closeModal: () => dispatch(hideModal()),
                    },
                    modalType: "alert",
                })
            );
            return;
        }

        try {
            handleApprove(id, approved);
        } catch (error) {
            console.error(
                `Error al ${approved ? "aprobar" : "desaprobar"} el registro ${id}:`,
                error
            );

            dispatch(
                showModal({
                    modalProps: {
                        open: true,
                        style: { width: "400px" },
                        title: "Error",
                        message: `Error al ${approved ? "aprobar" : "desaprobar"} los registros. Por favor, inténtelo de nuevo.`,
                        closeModal: () => dispatch(hideModal()),
                    },
                    modalType: "alert",
                })
            );
        }
    }, [items, handleApprove, dispatch]);

    // Índices O(1) — se reconstruyen solo cuando cambian las horas
    const objectiveIndex = useMemo(() => buildHoursIndex(objectiveHours), [objectiveHours]);
    const plannedIndex = useMemo(() => buildHoursIndex(plannedHours), [plannedHours]);

    const groupedItems = useMemo(() => {
        if (!items || items.length === 0) return [];

        const groupMap = new Map();

        items.forEach((item) => {
            const employeeId = item.employee?.id || "unknown";
            const day = item.day || moment(item.start).format("YYYY-MM-DD");
            const groupKey = `${employeeId}-${day}`;

            if (!groupMap.has(groupKey)) {
                groupMap.set(groupKey, {
                    id: groupKey,
                    employeeId,
                    employee: item.employee,
                    employeeName: item.employee
                        ? `${item.employee.name} ${item.employee.surname}`
                        : "Desconocido",
                    day,
                    date: moment(day).format("DD/MM/YYYY"),
                    records: [],
                    totalSeconds: 0,
                    balance: 0,
                    expectedWorkTime: item.expected_work_time || 28800,
                    hasAlert: false,
                    allAlerts: [],
                    alertIds: new Set(),
                });
            }

            const group = groupMap.get(groupKey);
            group.records.push(item);

            if (item.expected_work_time !== undefined && item.expected_work_time !== null) {
                group.expectedWorkTime = item.expected_work_time;
            }

            if (item.start && item.end) {
                const startMoment = moment(item.start);
                const endMoment = moment(item.end);
                const seconds = endMoment.diff(startMoment, "seconds");
                group.totalSeconds += seconds;
            } else if (item.seconds_worked) {
                group.totalSeconds += item.seconds_worked;
            }

            if (item.balance !== undefined && item.balance !== null) {
                group.balance = item.balance;
            }

            if (item.alerts && item.alerts.length > 0) {
                item.alerts.forEach((alert) => {
                    if (!group.alertIds.has(alert.id)) {
                        group.allAlerts.push(alert);
                        group.alertIds.add(alert.id);
                        group.hasAlert = true;
                    }
                });
            }
        });

        groupMap.forEach((group) => {
            const dayKey = moment(group.day).format("YYYY-MM-DD");
            const lookupKey = `${group.employeeId}_${dayKey}`;

            const objValue = objectiveIndex[lookupKey] || 0;
            const planValue = plannedIndex[lookupKey] || 0;

            group.objectiveHoursValue = objValue;
            group.plannedHoursValue = planValue;

            if (group.balance === 0) {
                const hoursToCompare =
                    timeRegistrationBalanceCalculation === "TARGET_HOURS"
                        ? objValue
                        : planValue;

                if (group.totalSeconds > 0 && hoursToCompare > 0) {
                    const balanceObj = getBalanceHours(hoursToCompare, group.totalSeconds);
                    group.balanceObj = balanceObj;
                    group.balance = (balanceObj.short_raw || 0) * 3600;
                } else {
                    group.balance = null;
                    group.balanceObj = null;
                }
            }
        });

        return Array.from(groupMap.values()).sort((a, b) => {
            if (a.employeeName !== b.employeeName) {
                return a.employeeName.localeCompare(b.employeeName);
            }
            return moment(a.day).diff(moment(b.day));
        });
    }, [items, objectiveIndex, plannedIndex, timeRegistrationBalanceCalculation]);

    return {
        items,
        loading,
        loadingMore,
        hasMore,
        fetchMoreData,
        handleSort,
        applySort,
        sortConfig,
        handleApprovalWithError,
        handleHideModal,
        groupedItems,
        showRestTime,
        timeRegistrationBalanceCalculation,
        plannedShifts,
        approvingIds,
    };
}; 